import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { CouponStatus, Plan, Subscription } from 'js/src/client';
import type {
    ConfirmationModalData,
    PlanSwitchErrorInfo,
    RetryUpgradeInfo,
    UPGRADE_STATUS,
} from 'js/src/components/pricing/types';

// Define a type for the slice state
export interface PricingState {
    selectedMonthly: boolean;
    monthlyPlans: Plan[];
    annualPlans: Plan[];
    couponStatus: CouponStatus | null;
    userHasPaymentMethodSetup: boolean | null;
    showSubscriptionChangeConfirmation: boolean;
    changingSubscription: boolean;
    upgradeStatus: UPGRADE_STATUS | null;
    retryUpgradeInfo: RetryUpgradeInfo | null;
    subscriptionDetails: Subscription | null;
    confirmationModalData: ConfirmationModalData;
    showPlanSwitchErrorModal: boolean;
    planSwitchErrorInfo: PlanSwitchErrorInfo | null;
}

// Define the initial state using that type
const initialState: PricingState = {
    selectedMonthly: false,
    monthlyPlans: [],
    annualPlans: [],
    couponStatus: null,
    userHasPaymentMethodSetup: null,
    showSubscriptionChangeConfirmation: false,
    changingSubscription: false,
    upgradeStatus: null,
    retryUpgradeInfo: null,
    subscriptionDetails: null,
    confirmationModalData: {
        plan: {} as Plan,
        email: '',
    },
    showPlanSwitchErrorModal: false,
    planSwitchErrorInfo: null,
};

export const pricingSlice = createSlice({
    name: 'pricing',
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setSelectedMonthly: (state, action: PayloadAction<boolean>) => {
            state.selectedMonthly = action.payload;
        },
        setMonthlyPlans: (state, action: PayloadAction<Plan[]>) => {
            state.monthlyPlans = action.payload;
        },
        setAnnualPlans: (state, action: PayloadAction<Plan[]>) => {
            state.annualPlans = action.payload;
        },
        setCouponStatus: (state, action: PayloadAction<CouponStatus | null>) => {
            state.couponStatus = action.payload;
        },
        setUserHasPaymentMethodSetup: (state, action: PayloadAction<boolean | null>) => {
            state.userHasPaymentMethodSetup = action.payload;
        },
        setShowSubscriptionChangeConfirmation: (state, action: PayloadAction<boolean>) => {
            state.showSubscriptionChangeConfirmation = action.payload;
        },
        setChangingSubscription: (state, action: PayloadAction<boolean>) => {
            state.changingSubscription = action.payload;
        },
        setUpgradeStatus: (state, action: PayloadAction<UPGRADE_STATUS | null>) => {
            state.upgradeStatus = action.payload;
        },
        setRetryUpgradeInfo: (state, action: PayloadAction<RetryUpgradeInfo | null>) => {
            state.retryUpgradeInfo = action.payload;
        },
        setSubscriptionDetails: (state, action: PayloadAction<Subscription | null>) => {
            state.subscriptionDetails = action.payload;
        },
        setConfirmationModalData: (state, action: PayloadAction<ConfirmationModalData>) => {
            state.confirmationModalData = action.payload;
        },
        setShowPlanSwitchErrorModal: (state, action: PayloadAction<boolean>) => {
            state.showPlanSwitchErrorModal = action.payload;
        },
        setPlanSwitchErrorInfo: (state, action: PayloadAction<PlanSwitchErrorInfo | null>) => {
            state.planSwitchErrorInfo = action.payload;
        },
    },
});

export const {
    setSelectedMonthly,
    setMonthlyPlans,
    setAnnualPlans,
    setCouponStatus,
    setUserHasPaymentMethodSetup,
    setShowSubscriptionChangeConfirmation,
    setChangingSubscription,
    setUpgradeStatus,
    setRetryUpgradeInfo,
    setSubscriptionDetails,
    setConfirmationModalData,
    setShowPlanSwitchErrorModal,
    setPlanSwitchErrorInfo,
} = pricingSlice.actions;

export const pricingReducer = pricingSlice.reducer;
